import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Loader2, Save } from 'lucide-react';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { DEPARTMENTS, TICKET_TYPES } from './constants';
import MatchIcon from './MatchIcon';
import TaxonomyField from './TaxonomyField';

/**
 * Ground-truth capture for one decision.
 *
 * This is the only thing in BOSS that can write `human_department` /
 * `human_ticket_type` / `human_owner`, which are the inputs to the ≥90%
 * agreement number the AppDirect engagement gates on. Two rules follow from
 * that and are load-bearing, not stylistic:
 *
 * 1. **Only an explicit click or keypress writes.** The pickers below are
 *    pre-filled with the AI's own answer so that "the AI was right" is one
 *    action instead of three — but a pre-fill that was never confirmed is not
 *    a human judgement, and persisting one would manufacture agreement out of
 *    nothing. Nothing here saves on mount, on decision change, or on close.
 *
 * 2. **Never write a field the human did not answer.** "Agree" copies only the
 *    AI values that actually exist; a null `ai_department` cannot be agreed
 *    with, so it is left unlabelled rather than written as null-matches-null.
 *
 * The panel is rendered only for users holding `ai_dispatcher.decisions.label`
 * — see DecisionDrawer — so there is no disabled-control dead end.
 */

// FastAPI hands back `detail` as a string for HTTPException and as a list of
// {loc, msg} objects for a 422. Rendering the raw object gives "[object
// Object]" in front of whoever is labelling, so flatten both shapes.
function errorText(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length) {
    const msgs = detail.map((d) => d?.msg).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  return fallback;
}

function ShortcutHint({ keys }) {
  return (
    <kbd className="ml-1.5 px-1.5 py-0.5 rounded border border-boss-border bg-boss-elevated text-[10px] font-medium text-boss-text-muted">
      {keys}
    </kbd>
  );
}

// Plain letter shortcuts must not fire while someone is typing a name into the
// owner box, or while a native select has focus and is consuming keystrokes
// for type-ahead.
function isTypingTarget(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

export default function LabelPanel({
  decision: d,
  onLabeled,
  onAdvance,
  hasNext,
  advanceAfterSave,
  onAdvanceAfterSaveChange,
}) {
  const isLabelled = Boolean(d.human_labeled_at || d.human_department || d.human_ticket_type || d.human_owner);

  // Seed from the existing human label when there is one, otherwise from the
  // AI's answer. Either way this is display state until an explicit save.
  const [dept, setDept] = useState(d.human_department ?? d.ai_department ?? '');
  const [ticketType, setTicketType] = useState(d.human_ticket_type ?? d.ai_ticket_type ?? '');
  const [owner, setOwner] = useState(d.human_owner ?? d.ai_owner ?? '');
  // Optional, never pre-filled from the AI — the AI's reasoning is its own
  // section of the drawer; this is the labeller's.
  const [reasoning, setReasoning] = useState(d.human_reasoning ?? '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  const submit = useCallback(
    async (payload) => {
      if (saving) return;
      setSaving(true);
      setError(null);
      try {
        const res = await api.patch(
          `${API_URL}/api/ai-dispatcher/decisions/${encodeURIComponent(d.dispatch_item_id)}`,
          payload,
        );
        const data = await res.json();
        if (!res.ok) throw new Error(errorText(data, 'Failed to save label'));

        // The contract returns the full updated decision, including the
        // server-computed dept_match / type_match / owner_match. Hand it
        // straight back so the row and the drawer show the server's verdict
        // rather than one this component guessed at. If the body is not a
        // recognisable decision, pass null and let the caller refetch — better
        // a round trip than a row that disagrees with the database.
        const updated = data && data.dispatch_item_id ? data : null;
        onLabeled(updated);

        setJustSaved(true);
        if (advanceAfterSave && hasNext) onAdvance();
      } catch (err) {
        setError(err?.message || 'Failed to save label');
      } finally {
        setSaving(false);
      }
    },
    [saving, d.dispatch_item_id, onLabeled, advanceAfterSave, hasNext, onAdvance],
  );

  // "Agree" == the human's answer is exactly what the AI said. Fields the AI
  // left null are omitted, not sent as null: there is nothing to agree with,
  // and writing them would count an unanswered field as a labelled one.
  const agreePayload = {};
  if (d.ai_department) agreePayload.human_department = d.ai_department;
  if (d.ai_ticket_type) agreePayload.human_ticket_type = d.ai_ticket_type;
  if (d.ai_owner) agreePayload.human_owner = d.ai_owner;
  const canAgree = Object.keys(agreePayload).length > 0;

  // Sent verbatim, never trimmed. Matching is whitespace-insensitive on the
  // server, so `'Cloud and SaaS '` still scores as agreement — but the stored
  // string is diagnostic evidence of what the labeller actually typed, and
  // cleaning it on the client would destroy that. Only a value that is empty
  // once trimmed becomes null, which is a question of whether anything was
  // answered at all, not a rewrite of the answer.
  const rawOrNull = (s) => (String(s).trim() === '' ? null : s);
  const savePayload = {
    human_department: rawOrNull(dept),
    human_ticket_type: rawOrNull(ticketType),
    human_owner: rawOrNull(owner),
  };
  // Checked before reasoning is added: a note on its own is not an answer,
  // and the server 400s a body that carries nothing else.
  const canSave = Object.values(savePayload).some((v) => v !== null);

  // Rides along with either button. Sent as null when blank so clearing the
  // box and saving again removes an earlier note.
  const reasoningValue = rawOrNull(reasoning);

  const handleAgree = useCallback(() => {
    if (canAgree && !saving) submit({ ...agreePayload, human_reasoning: reasoningValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAgree, saving, submit, d.ai_department, d.ai_ticket_type, d.ai_owner, reasoningValue]);

  const handleSave = useCallback(() => {
    if (canSave && !saving) submit({ ...savePayload, human_reasoning: reasoningValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSave, saving, submit, dept, ticketType, owner, reasoningValue]);

  useEffect(() => {
    const onKey = (e) => {
      // Ctrl/Cmd+Enter saves from anywhere, including mid-word in the owner
      // box — that is the whole point of having it.
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      const key = e.key.toLowerCase();
      if (key === 'a') {
        e.preventDefault();
        handleAgree();
      } else if (key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleAgree, handleSave]);

  return (
    <section>
      <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
        Label this decision
      </h3>

      <div className="glass-card p-4 space-y-3">
        {isLabelled ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-boss-text-secondary">
            <span className="boss-chip boss-chip-success">Labelled</span>
            <span>
              <MatchIcon val={d.dept_match} field="Department" />
              Dept
            </span>
            <span>
              <MatchIcon val={d.type_match} field="Ticket type" />
              Type
            </span>
            {d.human_value_off_enum && (
              <span className="boss-chip boss-chip-warning">Outside our taxonomy</span>
            )}
            <span>Change anything below and save again to correct it.</span>
          </div>
        ) : (
          <p className="text-xs text-boss-text-secondary">
            <span className="boss-chip boss-chip-neutral mr-2">Not labelled</span>
            Pre-filled with the AI's answer. Nothing is recorded until you press a button below.
          </p>
        )}

        <div className="space-y-3">
          <TaxonomyField
            id="label-department"
            label="Department it should have gone to"
            value={dept}
            onChange={setDept}
            options={DEPARTMENTS}
            disabled={saving}
            aiValue={d.ai_department}
            aiEmptyText="nothing — it produced no department"
          />

          <TaxonomyField
            id="label-ticket-type"
            label="Ticket type it should have been"
            value={ticketType}
            onChange={setTicketType}
            options={TICKET_TYPES}
            disabled={saving}
            aiValue={d.ai_ticket_type}
            aiEmptyText="nothing — it produced no ticket type"
          />

          <div>
            <label htmlFor="label-owner" className="block text-xs text-boss-text-secondary mb-1">
              Owner it should have gone to
            </label>
            <input
              id="label-owner"
              type="text"
              className="boss-input w-full px-3 py-2 text-sm"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Free text — a person's name"
              disabled={saving}
              autoComplete="off"
              // Matches the server's Field(max_length=200); without it a long
              // paste comes back as a 422 the labeller cannot act on.
              maxLength={200}
            />
            <p className="text-[11px] text-boss-text-muted mt-1">
              AI said: {d.ai_owner || 'nothing — no owner was assigned'}. Captured for later;
              owner agreement is not scored yet.
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="label-reasoning" className="block text-xs text-boss-text-secondary mb-1">
            Reasoning <span className="text-boss-text-muted">(optional)</span>
          </label>
          {/* A textarea counts as a typing target, so the A / S shortcuts stay
              quiet while someone writes here; Ctrl+Enter still saves. */}
          <textarea
            id="label-reasoning"
            className="boss-input w-full px-3 py-2 text-sm resize-y"
            rows={2}
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            placeholder="Why was the AI's answer wrong?"
            disabled={saving}
            // Matches the server's Field(max_length=2000).
            maxLength={2000}
          />
        </div>

        {/* Buttons after every field, reasoning included: both submit whatever
            is in the form at click time, so a note typed after clicking is lost. */}
        {error && (
          <div className="boss-alert-danger rounded-lg p-3 flex items-start gap-2">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <p className="text-xs text-boss-text">{error}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            className="btn-primary text-xs disabled:opacity-40"
            onClick={handleAgree}
            disabled={!canAgree || saving}
            title={
              canAgree
                ? "Record the AI's answer as the human answer"
                : 'The AI produced no department, ticket type or owner to agree with'
            }
          >
            {saving ? (
              <Loader2 size={14} className="inline mr-1 animate-spin" />
            ) : (
              <Check size={14} className="inline mr-1" />
            )}
            AI was right
            <ShortcutHint keys="A" />
          </button>

          <button
            type="button"
            className="btn-secondary text-xs disabled:opacity-40"
            onClick={handleSave}
            disabled={!canSave || saving}
            title={
              canSave
                ? 'Record the answers selected above'
                : 'Answer at least one field before saving'
            }
          >
            <Save size={14} className="inline mr-1" />
            Save these answers
            <ShortcutHint keys="S" />
          </button>

          {justSaved && !saving && !error && (
            <span className="text-xs text-[var(--boss-success-text)]">Saved</span>
          )}
        </div>

        <label className="flex items-center gap-2 text-xs text-boss-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={advanceAfterSave}
            onChange={(e) => onAdvanceAfterSaveChange(e.target.checked)}
            className="rounded border-boss-border"
          />
          Move to the next decision after saving
        </label>
      </div>
    </section>
  );
}
